import { SearchRegistry } from '../types';

export const backofficeSearchEntries: SearchRegistry = {
  '/backoffice/overview': {
    keywords: [
      'backoffice',
      'back office',
      'internal backoffice',
      'overview',
      'admin overview',
      'ryd internal',
      'operations dashboard',
      'backoffice landing',
      'backoffice home',
      'internal admin',
    ],
    questions: [
      'Where is the internal backoffice landing page?',
      'How do I open the ryd internal admin tools?',
    ],
  },
  '/backoffice/bmw/readiness-check': {
    keywords: [
      'bmw',
      'bmw readiness check',
      'bmw health check',
      'bmwfleet',
      'bmw readiness',
      'bmw setup check',
      'bmw core sponsor',
      'bmw delta sponsor',
      'create delta sponsor',
      'permission groups check',
      'bmw go-live check',
    ],
    questions: [
      'Is bmwfleet set up correctly in this environment?',
      'How do I check the BMW sponsors, features and permission groups?',
      'How do I create a missing BMW delta sponsor?',
    ],
  },
  '/backoffice/bmw/how-it-works': {
    keywords: [
      'bmw how it works',
      'bmw readiness check explained',
      'bmw checks',
      'bmwfleet checks',
      'bmw feature requirements',
      'bmw sponsors and features',
      'bmw delta sponsors',
      'bmwfleet-nfc-eu',
      'bmw delta sponsor template',
      'bmw nfc delta',
      'what does the bmw readiness check check',
    ],
    questions: [
      'What does the BMW readiness check check?',
      'Which features must the bmwfleet core sponsor have?',
      'How many delta sponsors does BMW need?',
      'Why is a BMW check UNKNOWN?',
    ],
  },
  '/backoffice/bmw/pages': {
    keywords: [
      'bmw pages',
      'bmw pages overview',
      'bmw pages and access',
      'bmw roles',
      'bmwfleet roles',
      'bmwfleet user group',
      'bmw partner hub access',
      'bmw circle k pcs access',
      'bmw fuel card pages',
      'which role for bmw',
    ],
    questions: [
      'Which pages does the BMW project have?',
      'Which role does a BMW user need for a page?',
      'Which roles and user groups open the BMW Partner Hub?',
      'Who can open the Circle K PCS pages?',
    ],
  },
  '/backoffice/sponsors': {
    keywords: [
      'sponsors',
      'sponsor management',
      'fleet sponsor',
      'sponsor configuration',
      'sponsor list',
      'tenant',
      'available sponsors',
      'search sponsors',
      'create sponsor',
      'sponsor admin',
      'sponsor view',
      'sponsor form',
      'sponsor table',
      'sponsor pagination',
    ],
    questions: [
      'How do I look up or edit a sponsor?',
      'Where do I configure a fleet sponsor?',
      'How do I create a new sponsor?',
      'How do I search for a sponsor by name?',
    ],
    faqs: [
      {
        q: 'What is a sponsor in ryd?',
        a: 'A sponsor is a fleet company tenant. Each sponsor groups its own users, accounts, vehicles, feature configurations and limits, and is the top-level identifier for fleet data.',
      },
      {
        q: 'How do I create a new sponsor?',
        a: 'Open Backoffice → Sponsors. With the Sponsor Admin role, the Create Sponsor button opens the SponsorForm; fill in the sponsor metadata and feature pack and save. The sponsor appears immediately in the Available Sponsors list.',
      },
    ],
  },
  '/backoffice/sponsor-bulk-update': {
    keywords: [
      'sponsor bulk update',
      'bulk update sponsors',
      'batch update sponsors',
      'bulk feature update',
      'batch processing',
      'batch journey',
      'bulk update journey',
      'bulk operation',
      'mass update',
      'add feature to many sponsors',
      'sponsor batch',
      'find sponsor by id',
      'exact sponsor id',
      'must include feature',
      'must exclude feature',
      'sponsor feature filters',
      '100 sponsors',
      'sponsor mirror search',
      'cloned sponsor data',
    ],
    questions: [
      'How do I update a feature on multiple sponsors at once?',
      'How do I find one sponsor by its exact sponsor ID?',
      'How do I include or exclude sponsors by feature?',
      'How many sponsors can I update in one journey?',
    ],
    faqs: [
      {
        q: 'How does a sponsor bulk update work?',
        a: 'Open Backoffice → Bulk Update Sponsors. Filter sponsors (e.g. by feature), select up to 100, define one feature change with a business reason, and confirm. The backend updates max. 2 sponsors in parallel, verifies each update automatically a few seconds later, and every API call is audit logged. The resulting journey records progress and allows individual retries or manual verification.',
      },
      {
        q: 'Does a bulk update keep running when I close the page?',
        a: 'Yes. Execution runs in the backend; the page only polls progress. If processing was interrupted, the journey detail page shows a Continue button that resumes exactly where it stopped — already updated sponsors are never updated twice.',
      },
      {
        q: 'Why can I select only 100 sponsors?',
        a: 'A bulk update journey is limited to 100 sponsors so account conflicts and the impact on accounts assigned to those sponsors remain reviewable. Split larger changes into multiple journeys with the include and exclude filters.',
      },
      {
        q: 'Does sponsor search use live sponsor data?',
        a: 'The first search step uses the portal sponsor clone for fast results. The clone is refreshed hourly and immediately after ryd portal changes; bulk updates themselves use the backend, not the clone.',
      },
    ],
  },
  '/backoffice/sponsor-bulk-update/journeys': {
    keywords: [
      'bulk update journeys',
      'sponsor bulk update history',
      'sponsor update journey',
      'failed sponsor bulk update',
      'bulk update results',
      'audited sponsor updates',
      'sponsor update business reason',
      'bulk update creator',
      'completed bulk update',
    ],
    questions: [
      'Where do I see sponsor bulk update journey history?',
      'How do I find a failed sponsor bulk update journey?',
      'Where can I see who started a sponsor bulk update?',
    ],
    faqs: [
      {
        q: 'What is stored in a sponsor bulk update journey?',
        a: 'Each journey stores its business reason, creator, status, totals, and per-sponsor update and verification results. Open a journey to inspect errors and API evidence.',
      },
    ],
  },
  '/backoffice/sponsor-bulk-update/journeys/:journeyId': {
    keywords: [
      'bulk update journey detail',
      'sponsor update progress',
      'sponsor update verification',
      'retry sponsor update',
      'bulk update evidence',
      'continue sponsor journey',
      'skip failed sponsor',
      'verify sponsor update',
      'sponsor api request response',
    ],
    questions: [
      'How do I review one sponsor bulk update journey?',
      'Where can I retry or verify a sponsor update?',
      'How do I continue an interrupted sponsor bulk update?',
    ],
    faqs: [
      {
        q: 'What can I do when one sponsor fails in a bulk update journey?',
        a: 'Open the sponsor in the journey detail page to inspect its error and API evidence. With sponsors_admin access, retry or skip the sponsor and run verification again where needed.',
      },
    ],
  },
  '/backoffice/features': {
    keywords: [
      'features',
      'feature management',
      'feature flags',
      'feature pack',
      'feature pack tag',
      'featurePackTag',
      'featureconfigurations',
      'feature configuration',
      'feature toggle',
      'add feature configuration',
      'fuel filters',
      'available products',
      'connected accounts',
      'feature limits',
      'sponsor features',
    ],
    questions: [
      'How do I enable a feature for a sponsor?',
      'Where do I see which features a fleet has access to?',
      'What does featurePackTag mean?',
      'How do I add a new feature configuration to a sponsor?',
    ],
    faqs: [
      {
        q: 'How do I check which features a fleet sponsor has enabled?',
        a: 'Open Backoffice → Sponsors & Features → Features. Each entry shows the available features and their configuration. To filter by sponsor, look up the sponsor first.',
      },
      {
        q: 'How do I add a new feature configuration?',
        a: 'On the Features page use Add Feature Configuration. The wizard guides you through fuel filters, available products and connected accounts; the resulting configuration is attached to the sponsor and applied immediately.',
      },
    ],
  },
  '/backoffice/list-price': {
    keywords: [
      'list price',
      'list prices',
      'list-fix price',
      'fix price',
      'pricing overview',
      'price configuration',
      'partner pricing',
      'list price management overview',
      'aral',
      'fleet invoicing pricing',
      'bigquery pricing',
      'odoo pricing',
      'management modules',
      'fix pricing',
      'price data collection',
      'fuel pricing system',
    ],
    questions: [
      'Where do I see and configure list prices for partners?',
      'How does the list/fix price system work?',
      'How does ryd ingest list prices from Aral?',
    ],
    faqs: [
      {
        q: 'How does the List Price Management system work end-to-end?',
        a: 'It collects price data from partner sources (e.g. Aral), applies adjustments and partner discounts, and feeds the pricing foundation for fleet invoicing via Google BigQuery and Odoo. The List Price Overview page exposes the six management modules: Price History, Incoming Emails, Contact Management, Price Adjustments, Partner Discounts and Email Preview.',
      },
    ],
  },
  '/backoffice/list-price/price-history': {
    keywords: [
      'price history',
      'list price history',
      'price changes',
      'historical prices',
      'price audit',
      'price log',
      'daily summaries',
      'price trends',
      'source records',
      'fix records',
      'aral price history',
    ],
    questions: [
      'How do I see the history of a list price?',
      'When was the last price change for a partner?',
      'Who changed a list price and when?',
      'How do I compare source vs. processed fix records?',
    ],
  },
  '/backoffice/list-price/partner-incoming-emails': {
    keywords: [
      'incoming emails',
      'partner emails',
      'list price emails',
      'price update emails',
      'inbound email',
      'email parsing',
      'aral email',
      'price email ingestion',
      'email log',
      'incoming email log',
    ],
    questions: [
      'Where do I see the price-update emails received from partners?',
      'How do I check which partner emails arrived for list prices?',
      'Why was a price-update email rejected?',
    ],
  },
  '/backoffice/list-price/contact-management': {
    keywords: [
      'contact management',
      'partner contacts',
      'contacts',
      'list price contacts',
      'contact list',
      'recipient management',
      'cc recipients list price',
      'distribution list',
      'partner email recipients',
    ],
    questions: [
      'How do I add or edit a list-price contact for a partner?',
      'Where do I manage the recipients for price update communication?',
      'How do I add CC recipients to list-price emails?',
    ],
  },
  '/backoffice/list-price/price-adjustment': {
    keywords: [
      'price adjustment',
      'price adjustments',
      'price correction',
      'manual price update',
      'price override',
      'adjust list price',
      'price adjustment factor',
      'partner adjustment',
      'price markup',
    ],
    questions: [
      'How do I manually adjust a list price?',
      'Where can I correct a wrong price for a partner?',
      'How do I configure a markup for a partner?',
    ],
  },
  '/backoffice/list-price/partner-discount': {
    keywords: [
      'partner discount',
      'partner discounts',
      'list price discount',
      'discount configuration',
      'rebate',
      'fleet discount',
      'discount %',
      'fix discount',
      'percentage discount',
    ],
    questions: [
      'How do I configure a discount for a fleet or API partner?',
      'Where do I set up partner-specific rebates?',
      'How are discounts applied to a partner price?',
    ],
  },
  '/backoffice/list-price/email-preview': {
    keywords: [
      'email preview',
      'email test',
      'list price email preview',
      'preview email',
      'test email',
      'email template preview',
      'send test email',
      'preview send',
    ],
    questions: [
      'How do I preview the list-price email a partner will receive?',
      'How do I send a test list-price email?',
    ],
  },
  '/backoffice/api-partner-discount': {
    keywords: [
      'api partner discount',
      'api partner discounts',
      'api partner rebate',
      'api discount configuration',
      'discount for api partners',
      'api partner discount dashboard',
      'discount eligibility',
      'fix discount',
      'discount tiers',
      'filter discount rules',
      'discount partner filter',
      'discount country filter',
      'discount brand filter',
      'active discount rules',
      'inactive discount rules',
      'share discount search',
      'discount configuration checklist',
      'live discount configuration',
    ],
    questions: [
      'Where are the API partner discount rules?',
      'How do I see the discount that applies to a specific API partner?',
      'How do I filter API partner discounts by country or brand?',
      'How do I share a filtered API partner discount search?',
    ],
    faqs: [
      {
        q: 'How do I find existing API partner discount rules?',
        a: 'Open Backoffice → API Partner Discounts. Filter by partner, status, country, or brand, then run the search to compare product scope, value, validity, and current status.',
      },
      {
        q: 'How do I share an API partner discount search?',
        a: 'Apply the required dashboard filters and select Share This Search. The copied URL contains the current partner, status, country, and brand filters.',
      },
      {
        q: 'Who can manage API partner discount rules?',
        a: 'Internal ryd users can view and search discount rules. Creating, editing, activating, or deactivating rules requires the discount_management_admin role.',
      },
    ],
    urlParameters: ['active', 'userGroup', 'country', 'brand'],
  },
  '/backoffice/api-partner-discount/new': {
    keywords: [
      'add api partner discount',
      'create api partner discount',
      'new discount rule',
      'api partner discount form',
      'partner discount scope',
      'normalized product names',
      'ALL_COUNTRIES',
      'ALL_BRANDS',
      'ALL_PRODUCTS',
      'discount net per unit',
      'discount unit',
      'discount validity',
      'related discount rules',
      'discount overlap',
      'discount conflict',
    ],
    questions: [
      'How do I set up a discount for an API partner?',
      'How do I create an API partner discount rule?',
      'How do I avoid overlapping API partner discount rules?',
      'What does ALL_COUNTRIES or ALL_PRODUCTS mean for a discount?',
    ],
    faqs: [
      {
        q: 'How do I create an API partner discount rule?',
        a: 'Open Backoffice → API Partner Discount → Add Discount. Define the partner, country, brand, product group, normalized products, net discount per unit, unit, and validity, then review related rules before saving.',
      },
      {
        q: 'How do I avoid conflicting API partner discounts?',
        a: 'Review the related rules shown for the selected partner before saving. Conflicts occur when partner, country, product group, validity, products, tax rate, and brand coverage overlap.',
      },
      {
        q: 'Is the API partner discount entered in euros or cents?',
        a: 'Enter Discount Net Per Unit in euros per unit, not cents. For example, 0.015 means 1.5 cents per unit.',
      },
    ],
  },
  '/backoffice/api-partner-discount/:id/edit': {
    keywords: [
      'edit api partner discount',
      'update discount rule',
      'change api partner discount',
      'activate api partner discount',
      'deactivate api partner discount',
      'discount conflict resolution',
      'related discount rules',
      'review discount overlap',
    ],
    questions: [
      'How do I edit an API partner discount rule?',
      'Why was my API partner discount not saved?',
      'How do I resolve an API partner discount conflict?',
    ],
    faqs: [
      {
        q: 'Why was my API partner discount not saved?',
        a: 'The rule may overlap with an existing discount. Review the listed conflicting rules, then adjust validity, products, tax rate, or brand coverage, or deactivate an existing conflicting rule where appropriate.',
      },
    ],
  },
  '/backoffice/email-domains': {
    keywords: [
      'email domains',
      'email domain management',
      'allowed domains',
      'domain whitelist',
      'corporate email domains',
      'email policy',
      'all email domains',
      'domain registry',
      'fleet email domains',
    ],
    questions: [
      'How do I manage allowed email domains?',
      'Where do I see the list of approved email domains for fleet users?',
    ],
  },
  '/backoffice/email-domains/add': {
    keywords: [
      'add email domain',
      'register domain',
      'new email domain',
      'whitelist domain',
      'allow domain',
      'email domain admin',
    ],
    questions: [
      'How do I add a new email domain to the allowed list?',
      'Where do I register a new corporate email domain?',
    ],
  },
  '/backoffice/order-lookup/audit-logs': {
    keywords: [
      'order lookup',
      'audit logs',
      'order audit log',
      'lookup audit',
      'who searched',
      'access log',
      'audit trail',
      'audit logs section',
      'lookup audit trail',
      'operator audit',
    ],
    questions: [
      'Who looked up order X recently?',
      'Where are the audit logs for order lookups?',
      'How do I see who has accessed customer support data?',
    ],
    faqs: [
      {
        q: 'How do I see who has accessed a specific order?',
        a: 'Open Backoffice → Order Lookup → Audit Logs and filter by order ID or user. The log records every lookup with timestamp and operator.',
      },
    ],
  },
  '/backoffice/connect-tracking': {
    keywords: [
      'ryd connect',
      'connect tracking',
      'fleet api tracking',
      'frontend backend correlation',
      'connect journey',
      'partner webview analytics',
      'fleet api audit',
    ],
    questions: [
      'How do I trace a ryd Connect user journey?',
      'Where can I link frontend Connect events with fleet API audit logs?',
    ],
  },
  '/backoffice/order-lookup/analytics': {
    keywords: [
      'order lookup analytics',
      'lookup analytics',
      'support analytics',
      'lookup statistics',
      'lookup volume',
      'lookup metrics',
      'agent activity',
      'audit analytics',
    ],
    questions: [
      'How many order lookups happened last week?',
      'Where do I see lookup volume per support agent?',
      'Which operators run the most lookups?',
    ],
  },
  '/backoffice/administration/metadata-sync': {
    keywords: [
      'metadata sync',
      'metadata synchronization',
      'sync metadata',
      'product metadata sync',
      'station metadata sync',
      'manual sync',
      'sync trigger',
      'sync status',
      'metadata refresh',
    ],
    questions: [
      'How do I trigger a metadata sync?',
      'Where do I check whether metadata has been synced?',
      'How long does a manual metadata sync take?',
    ],
  },
  '/backoffice/administration/cloudflare-firebase-remote-config': {
    keywords: [
      'cloudflare',
      'cloudflare rules',
      'firebase remote config',
      'remote config',
      'feature toggles',
      'edge rules',
      'WAF',
      'cloudflare configuration',
      'qs_secret',
      'pay.ryd.one',
      '/auth/login/local',
      'referer header',
      'cloudflare waf rule',
      'rotation 15 minutes',
      'google cloud scheduler',
      'operations_admin',
      'atomic rotation',
      'ryd-pay-webapp project',
      'webapp secret',
      'login secret',
    ],
    questions: [
      'Where do I edit Cloudflare rules?',
      'How do I update Firebase Remote Config from the portal?',
      'What is qs_secret and how is it rotated?',
      'How do I manually rotate the qs_secret?',
    ],
    faqs: [
      {
        q: 'What is the qs_secret and how is it rotated?',
        a: "qs_secret is a dynamic query parameter the pay.ryd.one webapp adds to /auth/login/local; a Cloudflare WAF rule blocks login requests from that referer that don't carry the correct value (HTTP 401). The secret rotates automatically every 15 minutes via Google Cloud Scheduler and updates Firebase Remote Config and the Cloudflare WAF rule atomically. operations_admin can also rotate it manually from this page.",
      },
    ],
  },
  '/backoffice/administration/token-management': {
    keywords: [
      'token management',
      'token lookup',
      'auth token',
      'firebase token',
      'session token',
      'inspect token',
      'decode token',
      'jwt',
      'x-txn-auth-token',
      'lookup auth token',
      'token owner',
      'token status',
      'token user',
    ],
    questions: [
      'How do I decode a token?',
      'Where do I look up the contents of an auth token?',
      'How do I find the owner of a specific x-txn-auth-token?',
    ],
    faqs: [
      {
        q: 'How do I find the owner of an auth token?',
        a: 'Open Backoffice → Administration → Token Lookup, paste the full x-txn-auth-token (UUID) and run the search. The result shows the token owner, status, and the user/account the token was issued for.',
      },
    ],
  },
  '/backoffice/partner-filter-rules': {
    keywords: [
      'partner filter rules',
      'station visibility',
      'station visibility policies',
      'which stations can a partner see',
      'hide stations from partner',
      'filter stations',
      'partner station filter',
      'rule engine',
      'partner filter overview',
      'configuration health',
      'default policies',
      'ryd internal',
    ],
    questions: [
      'How does ryd decide which stations a partner can see?',
      'Where do I configure station visibility for a partner?',
      'Which default policies must exist for the rule engine?',
      'Why does a partner see no stations at all?',
    ],
    faqs: [
      {
        q: 'How does ryd decide whether a partner sees a station?',
        a: 'Two gates. First the policy gate: the partner or customer policy and the platform policy are combined with a strict AND, and any matching enabled DENY rule hides the station regardless of ALLOW rules. Then the registry gate: a restricted-entity entry hides the station for everyone unless an exemption names that client.',
      },
      {
        q: 'Why does a client suddenly see nothing?',
        a: 'Almost always a policy with a DENY default and no enabled ALLOW rule, or a missing pass-through default policy for one resolution key. The Partner Filter Rules overview flags both under Configuration Health.',
      },
      {
        q: 'Who can view and change partner filter rules?',
        a: 'Every internal ryd user can open and read the section. Creating, editing, deleting policies, rules and registry entries, and applying predefined rule sets, requires the partner_filter_admin role. Every change is written to an audit trail on success and on failure.',
      },
    ],
  },
  '/backoffice/partner-filter-rules/policies': {
    keywords: [
      'station visibility policies',
      'partner filter policies',
      'policy list',
      'resolution key',
      'key value',
      'default decision',
      'deny by default',
      'allow by default',
      'whitelist stations',
      'blacklist stations',
      'policy rules',
      'allow rule',
      'deny rule',
      'shop type filter',
      'country filter',
      'brand filter',
    ],
    questions: [
      'How do I create a station visibility policy for a partner?',
      'How do I stop one partner from seeing a brand or country?',
      'What is the difference between a DENY default and an ALLOW default?',
      'How do I disable a rule without deleting it?',
    ],
    faqs: [
      {
        q: 'What is a resolution key?',
        a: 'The dimension a policy is keyed on: PARTNER (the portal user group, e.g. mercedes), CUSTOMER_ID, or PLATFORM_TYPE. Each key needs one pass-through default policy with the key value "default".',
      },
      {
        q: 'Why does adding an ALLOW rule not make a station visible?',
        a: 'A matching DENY rule always wins, in the same policy or in the other policy of the strict AND, and a restricted-entity entry beats every policy. Check the Decision Check page to see which policies resolve for the client.',
      },
      {
        q: 'Why should two carve-outs be two rules?',
        a: 'All conditions inside one rule are combined with AND. Blocking GAS in one country and WASH in another with a single rule would block the full cartesian product, so write one rule per carve-out.',
      },
    ],
  },
  '/backoffice/partner-filter-rules/restricted-entities': {
    keywords: [
      'restricted entity',
      'restricted entities',
      'registry',
      'restricted entity registry',
      'global station restriction',
      'hide station from everyone',
      'exemption',
      'exemptions',
      'contractual restriction',
      'disa',
    ],
    questions: [
      'How do I hide a brand from every partner?',
      'How do I add an exemption so one partner still sees a restricted brand?',
      'What is the difference between a policy and a restricted entity?',
    ],
    faqs: [
      {
        q: 'When should I use the registry instead of a policy?',
        a: 'Use the registry only when a restriction must also apply to partners who have not been onboarded yet. A registry entry hides the entity from every present and future client; a policy scopes one client.',
      },
      {
        q: 'How does a client see a restricted entity again?',
        a: 'Only through an exemption on that registry entry, naming the partner, customer id, or platform type. Policy ALLOW rules cannot override the registry.',
      },
      {
        q: 'Can I disable a registry entry?',
        a: 'No. There is no enabled flag; deleting the entry reveals its stations to every client immediately, which is why deletion asks for a typed confirmation and is recorded in the audit trail.',
      },
    ],
  },
  '/backoffice/partner-filter-rules/predefined': {
    keywords: [
      'predefined partner rules',
      'rule sets',
      'rule templates',
      'approved concept rules',
      'default policies',
      'foundation policies',
      'apply rule set',
      'mercedes rules',
      'e100 rules',
      'partner rule catalogue',
    ],
    questions: [
      'How do I create the three default pass-through policies?',
      'Where are the approved partner rule sets?',
      'How do I apply the Mercedes rule set?',
      'Why is a rule excluded from a rule set?',
    ],
    faqs: [
      {
        q: 'What are the foundation policies and why do they matter?',
        a: 'One pass-through default policy per resolution key (PARTNER, CUSTOMER_ID, PLATFORM_TYPE). Without them the strict AND across resolution keys blocks existing traffic as soon as any scoped policy exists, so create them before anything else.',
      },
      {
        q: 'Why does a rule set show rules that need input?',
        a: 'Rules classified as needing input or as anticipated carry no values yet, so they would match nothing. They are listed for completeness and excluded from the apply until the missing station ids or country is supplied.',
      },
    ],
  },
  '/backoffice/partner-filter-rules/decision-check': {
    keywords: [
      'decision check',
      'resolve policies',
      'which policies apply',
      'why is this station hidden',
      'test station visibility',
      'partner identifier',
      'platform type',
      'customer id lookup',
    ],
    questions: [
      'Which policies currently apply to a partner?',
      'How do I check my policy change worked?',
      'Why can a partner not see a specific station?',
    ],
    faqs: [
      {
        q: 'What does Decision Check answer?',
        a: 'Which policies the rule engine resolves for one client and which of their rules are active. It does not answer whether one specific station survives them: that needs a per-shop explanation endpoint the rule engine does not expose yet.',
      },
      {
        q: 'What does an empty resolve result mean?',
        a: 'No scoped policy matches that client, so the pass-through default policy for the dimension applies.',
      },
    ],
  },
  '/backoffice/partner-pricing': {
    keywords: [
      'partner pricing',
      'multi-channel pricing',
      'multichannel pricing',
      'what price does a customer get',
      'partner price resolution',
      'list price engine',
      'api partner discount engine',
      'pricing model',
      'list price partner',
      'pump price partner',
      'shopsiteprices',
      'cashier price fallback',
      'pop pricing',
      'ryd internal',
    ],
    questions: [
      'How is a partner price resolved for an API partner customer?',
      'Which engine applies to a LIST_PRICE or PUMP_PRICE sponsor?',
      'Why does an API partner customer get the cashier price?',
      'Where are partner pricing rules managed?',
    ],
    faqs: [
      {
        q: 'How is a partner price resolved?',
        a: 'Partner pricing lives in two engines keyed on the partner user group: the List Pricing Engine holds calculated partner list prices, the API Partner Discount Engine holds pump-price discounts. The sponsor pricing model (LIST_PRICE or PUMP_PRICE) decides which one applies. user-service publishes every rule together with the customer ids of its partner so POP can resolve by account id.',
      },
      {
        q: 'Why does a customer get the cashier price?',
        a: 'No rule matched in the engine that applies to the sponsor pricing model, so the cashier price (ShopSitePrices) is served. Use the Pricing Lookup with the customer id: an empty result there is the finding.',
      },
      {
        q: 'Can I change pricing rules in Partner Pricing?',
        a: 'No. The section is read-only. List prices and their partner discounts are managed under List/Fix Price, pump-price discounts under API Partner Discounts; both need discount_management_admin.',
      },
    ],
  },
  '/backoffice/partner-pricing/lookup': {
    keywords: [
      'pricing lookup',
      'partner pricing lookup',
      'customer price lookup',
      'customer id pricing',
      'which partner does this customer belong to',
      'resolved list price',
      'resolved discount',
      'list prices for customer',
      'api partner discounts for customer',
      'no pricing rule found',
      'no discount found',
      'pricing troubleshooting',
      'wrong price api partner',
    ],
    questions: [
      'What list price and discount does this customer get?',
      'Which partner user group does a customer id resolve to?',
      'Why does this API partner customer see a wrong price?',
      'How do I check that a new pricing rule reached the customer?',
    ],
    faqs: [
      {
        q: 'What does the Pricing Lookup show?',
        a: 'For one customer id and/or partner identifier: every list-price row the List Pricing Engine publishes for it and every active API partner discount, both server-paginated, plus the partner user group(s) the customer resolved to. Matching is exact, including case.',
      },
      {
        q: 'Why are there many rows for one discount rule?',
        a: 'The upstream feed publishes one row per country a rule covers, so one "all brands, all countries" rule shows a dozen rows. That is what POP receives.',
      },
      {
        q: 'I changed a rule and the lookup does not show it yet.',
        a: 'The feeds are downloaded from BigQuery and cached for five minutes. Each card shows when its feed was read; press Refresh Feeds to bypass the cache.',
      },
    ],
  },
  '/backoffice/notifications/overview': {
    keywords: [
      'notifications',
      'notification service',
      'push notifications',
      'notification dispatch',
      'app notifications',
      'mobile push',
    ],
    questions: [
      'Where do I manage the notifications the ryd app sends?',
      'How many notifications are stuck in the dispatch backlog?',
      'What is the notification dispatch service?',
    ],
  },
  '/backoffice/notifications/templates': {
    keywords: [
      'notification template',
      'push template',
      'push text',
      'push title',
      'notification wording',
      'notification locale',
      'german notification text',
      'deep link',
    ],
    questions: [
      'How do I change the wording of a push notification?',
      'Why can I not edit this notification template?',
      'Where do I add a German translation for a notification?',
      'Which campaigns use this notification template?',
    ],
  },
  '/backoffice/notifications/campaigns': {
    keywords: [
      'notification campaign',
      'reminder campaign',
      'nfc reminder',
      'receipt reminder',
      'odometer reminder',
      'campaign steps',
      'notification schedule',
    ],
    questions: [
      'How do I see the steps of a notification campaign?',
      'Why is my scheduled campaign not sending anything?',
      'How long after the trigger does each reminder go out?',
      'How do I activate a notification campaign?',
    ],
  },
  '/backoffice/notifications/promotion-campaigns': {
    keywords: [
      'promotion campaign',
      'promo campaign',
      'tag targeting',
      'users reachable',
      'campaign reach',
      'legacy backoffice campaigns',
      'push title',
      'push text',
      'campaign rank',
      'required tags',
      'excluded tags',
    ],
    questions: [
      'Where do I create a promotion campaign?',
      'How many users does this campaign reach?',
      'How do I change the push title and text of a promotion?',
      'Which campaigns target a given user tag?',
      'How many pushes did this campaign deliver?',
    ],
    faqs: [
      {
        q: 'Why does editing push text here not change my reminder notification?',
        a: 'There are two different things called campaign. Promotion campaigns are tag-targeted and carry their own push title and text. Reminder sequences that fire off order events are separate, and their wording comes from notification template locales — which currently have no editing API at all. Check the Notifications Knowledge Base page for the distinction.',
      },
    ],
  },
  '/backoffice/notifications/dispatches': {
    keywords: [
      'dispatch monitor',
      'notification status',
      'push sent',
      'push failed',
      'delivery log',
      'notification history',
      'why was no push sent',
    ],
    questions: [
      'Did we send a push notification to this user?',
      'Why did this notification fail?',
      'How do I find all notifications for an order?',
      'What does a skipped dispatch mean?',
    ],
  },
  '/backoffice/notifications/test-send': {
    keywords: [
      'test push',
      'send test notification',
      'try out push notification',
      'push preflight',
      'test notification to user',
      'check push token valid',
    ],
    questions: [
      'How do I send a test push notification to a user?',
      'Does this user have a valid push token?',
      'Which device would receive the notification?',
      'How can I check that push notifications still work?',
    ],
  },
  '/backoffice/notifications/push-tokens': {
    keywords: [
      'push token',
      'device token',
      'fcm token',
      'apns token',
      'token expired',
      'token deactivated',
      'registered devices',
    ],
    questions: [
      'Does this user have an active push token?',
      'Why was this push token deactivated?',
      'Which device receives notifications if the user has several?',
    ],
  },
  '/backoffice/notifications/knowledge-base': {
    keywords: [
      'notification knowledge base',
      'delivery funnel',
      'notification delivered',
      'notification opened',
      'push analytics',
      'firebase delivery',
    ],
    questions: [
      'How do I check whether a push notification was delivered?',
      'Did the user open the notification?',
      'What does each stage of the delivery funnel prove?',
      'Which notification fields does the engine ignore?',
    ],
  },
  '/backoffice/ai-assistants/support-chat/settings': {
    keywords: [
      'ai assistants',
      'support chat settings',
      'chat ai status',
      'chatbot settings',
      'end user ai',
      'ai status',
      'feature flags',
      'chat flags',
      'chatbot configuration',
      'answer model',
      'knowledge base corpus',
    ],
    questions: [
      'Where do I see whether the support chatbot is healthy?',
      'How do I change the support chat configuration?',
      'Which feature flags are active in the end-user AI?',
      'Which model answers customer chat questions?',
    ],
    faqs: [
      {
        q: 'Where do I check the support chat status and settings?',
        a: 'Open Internal Backoffice → AI Assistants → Chat Settings. The page reads /v1/health from ryd-end-user-ai and shows the environment, answer and classify models, knowledge-base corpus, and which feature flags are switched on. The CS-editable settings can be changed there directly.',
      },
    ],
  },
  '/backoffice/ai-assistants/support-chat/verification-code': {
    keywords: [
      'ai assistants',
      'chat verification code',
      'otp intercept',
      'one time code',
      'otp lookup',
      'support chat code',
      'verification code lookup',
      'agent mode code',
      'elevation code',
    ],
    questions: [
      'Where do I find the one-time code for the support chat?',
      'How do I look up an OTP for agent mode?',
      'The customer did not receive the chat verification code — what now?',
    ],
    faqs: [
      {
        q: 'How do I look up a support chat verification code?',
        a: 'Open Internal Backoffice → AI Assistants → Verification Code and search for the email. Codes are delete-on-read and only visible while code interception is enabled in ryd-end-user-ai (OTP_INTERCEPT_ENABLED); when it is off the page says so rather than being hidden.',
      },
    ],
  },
  '/backoffice/ai-assistants/support-chat/agent-whitelist': {
    keywords: [
      'ai assistants',
      'support chat agent whitelist',
      'cs whitelist',
      'agent mode',
      'elevated access',
      'elevation',
      'end user ai',
      'support chat',
      'chatbot agent access',
      'whitelist email',
      'cs elevated',
      'operations admin whitelist',
    ],
    questions: [
      'How do I give a CS agent access to Agent Mode in the support chat?',
      'Where do I add or remove an email from the support chat whitelist?',
      'How do I revoke a support agent’s elevated chat access?',
      'Who can see any customer’s data in the end-user support chat?',
    ],
    faqs: [
      {
        q: 'How do I whitelist a CS agent for Agent Mode?',
        a: 'Open Internal Backoffice → AI Assistants → Agent Whitelist and add the agent’s ryd-internal email (ryd.one or thinxnet.com). The agent can then request a one-time code in the end-user support chat and gets a fixed 15-minute, audited, read-only window over any customer’s data. Requires userGroup ryd and the operations_admin role. This page used to sit under Backoffice → Administration and later under Customer Support → Support Chat; both old URLs still redirect here.',
      },
      {
        q: 'How do I revoke a whitelisted agent?',
        a: 'Use Deactivate rather than Delete — it blocks the next elevation but keeps the entry and its history for auditing. Either way the change applies to the agent’s next code request; an elevation window already in progress is not revoked (worst case 15 minutes). The whitelist is per environment.',
      },
    ],
  },
  '/backoffice/ai-assistants/overview': {
    keywords: [
      'ai assistants',
      'ai assistants overview',
      'ai assistants home',
      'mia',
      'assistant training',
      'support chat tools',
      'ai operations',
    ],
    questions: [
      'Where do I find the AI assistant training and support chat tools?',
      'Which role do I need for AI Training or the support chat settings?',
    ],
  },
  '/backoffice/ai-assistants/how-it-works': {
    keywords: [
      'ai assistants how it works',
      'how it works',
      'request inbox process',
      'knowledge center vs training',
      'ai training process',
      'classification',
      'masking',
      'activation',
      'snapshot',
      'mia',
    ],
    questions: [
      'How do the Request Inbox, the knowledge center and AI training work together?',
      'What does the AI see of a customer mail, and what is masked?',
      'How does a correction reach Mia, the portal assistant or the partner assistants?',
    ],
  },
  '/backoffice/ai-assistants/knowledge-center/test': {
    urlParameters: [
      '?retriever={request-inbox|mia|mia-verified|corpus}',
      '?corpus={collection name}',
    ],
    keywords: [
      'test a question',
      'retrieval preview',
      'which articles does the ai use',
      'knowledge gap',
      'mia retrieval',
      'request inbox grounding',
      'knowledge center test',
    ],
    questions: [
      'Which knowledge articles would Mia use for this question?',
      'Why does the classifier not find the right article?',
    ],
  },
  '/backoffice/ai-assistants/knowledge-center': {
    keywords: [
      'ai assistants',
      'knowledge center',
      'ai knowledge base',
      'knowledge corpus',
      'corpora',
      'knowledge articles',
      'knowledge cards',
      'retrieval preview',
      'test a question',
      'what does the ai know',
      'kc-internal',
      'kc-partner',
      'kc-public',
      'mia knowledge',
    ],
    questions: [
      'What does the AI knowledge base contain?',
      'Which knowledge articles would the assistant find for this question?',
      'When was the knowledge center last published?',
    ],
  },
  '/backoffice/ai-assistants/training/cases': {
    urlParameters: [
      '?status={status}',
      '?areaId={area}',
      '?origin={origin}',
      '?q={text}',
      '?consumer={mia|portal|mcp}',
      '?audience={audience}',
    ],
    keywords: [
      'ai assistants',
      'mia training',
      'mia training cases',
      'training cases',
      'portal assistant training',
      'partner assistant training',
      'mcp training',
      'training audiences',
      'ai training',
      'chatbot training',
      'support chat training',
      'expected answer',
      'current answer',
      'regression case',
      'run on staging mia',
      'capture from session',
      'training v3',
      'confluence training table',
    ],
    questions: [
      'Where do I record a wrong Mia answer and the answer it should give?',
      'How do I re-run a question on staging Mia?',
      'Where is the Mia training table now that it left Confluence?',
    ],
    faqs: [
      {
        q: 'How do I teach Mia a better answer?',
        a: 'Open Internal Backoffice → AI Assistants → Training Cases, create a case with the question, Mia’s current answer (paste it, run it on staging Mia, or capture it from a session id) and the expected answer. Add hints, rules or commands to the case or its area; an admin approves and activates them per environment. Needs ai_operations_agent (admin for approval).',
      },
    ],
  },
  '/backoffice/ai-assistants/training/area-rules': {
    urlParameters: ['?areaId={area}'],
    keywords: [
      'ai assistants',
      'mia area rules',
      'mia hints',
      'mia rules',
      'mia commands',
      'allow-listed commands',
      'OFFER_TICKET',
      'LINK_ARTICLE',
      'NO_GOODWILL_PROMISE',
    ],
    questions: [
      'How do I add a rule for every Mia question about gift cards?',
      'Which commands can Mia training use?',
    ],
  },
  '/backoffice/ai-assistants/training/activation': {
    keywords: [
      'ai assistants',
      'mia activation',
      'training rules snapshot',
      'activate rules',
      'rollback mia rules',
      'eua_config training_rules',
    ],
    questions: [
      'How do approved Mia rules reach staging and production?',
      'How do I roll back Mia training rules?',
    ],
  },
  '/backoffice/ai-assistants/training/regression-runs': {
    keywords: [
      'ai assistants',
      'mia regression',
      'regression run',
      'pass rate',
      'eval harness',
      'new failures',
      'similarity score',
    ],
    questions: [
      'Did the latest Mia rule change break a training case?',
      'Where do I see the Mia regression pass rate?',
    ],
  },
  '/backoffice/ai-assistants/request-inbox': {
    urlParameters: ['?from={yyyy-mm-dd}&to={yyyy-mm-dd}'],
    keywords: [
      'request inbox',
      'info@ryd.one',
      'partner-support@ryd.one',
      'sources',
      'portal requests',
      'info mailbox',
      'info email',
      'inbox analytics',
      'email trends',
      'mail trends',
      'customer emails',
      'ticket drafts',
      'dry run',
      'odoo helpdesk routing',
      'email classification',
      'ai classification',
      'awaiting reply',
      'first response time',
      'posteingang',
      'info postfach',
    ],
    questions: [
      'How many requests does each source (info@ryd.one, partner-support, portal) receive per category?',
      'Which requests are still awaiting a reply?',
      'How often do colleagues agree with the AI ticket drafts?',
      'Does the Request Inbox create tickets in Odoo?',
    ],
    faqs: [
      {
        q: 'Does the Request Inbox create Odoo tickets?',
        a: 'Not in phase 1. The pipeline classifies each inbound request and stores a ticket draft showing exactly what would be created in Odoo Helpdesk (team, tags, priority, subject, partner). Nothing is sent to Odoo; colleagues review the drafts so rule precision is measured first.',
      },
      {
        q: 'Who can open the Request Inbox?',
        a: 'ryd internal users with ai_operations_viewer (read), ai_operations_agent (also correct classifications and review drafts) or ai_operations_admin (also taxonomy, routing rules, team map and feedback queue). Every message view is audit-logged.',
      },
    ],
  },
  '/backoffice/ai-assistants/request-inbox/messages': {
    urlParameters: [
      '?from={yyyy-mm-dd}&to={yyyy-mm-dd}',
      '?categoryId={id}',
      '?teamCode={teamCode}',
      '?action={action}',
      '?draftStatus={status}',
      '?replied=replied|awaiting',
      '?direction=inbound|outbound',
      '?q={text}',
    ],
    keywords: [
      'request inbox messages',
      'info@ryd.one messages',
      'info mails',
      'email list',
      'ticket draft',
      'draft status',
      'needs review',
      'answered in mailbox',
      'thread',
      'conversation',
      'classification',
      'correct classification',
      'agree draft',
      'skip draft',
      'odoo payload',
    ],
    questions: [
      'Where do I see the mails sent to info@ryd.one or partner-support@ryd.one?',
      'How do I review a ticket draft for an inbound request?',
      'How do I correct a wrong AI classification?',
      'Where do I see the whole conversation of a request?',
    ],
    faqs: [
      {
        q: 'How do I review a ticket draft?',
        a: 'Open Backoffice → AI Assistants → Request Inbox → Messages, open a message and use the "Ticket draft – not sent to Odoo" card: Agree, Correct (team, tags, priority, action, subject) or Skip. This needs ai_operations_agent or ai_operations_admin and never writes to Odoo in phase 1.',
      },
    ],
  },
  '/backoffice/ai-assistants/request-inbox/taxonomy': {
    keywords: [
      'request inbox taxonomy',
      'email categories',
      'topics',
      'areas',
      'labels',
      'suggested terms',
      'merge category',
      'rename category',
      'disable category',
    ],
    questions: [
      'Where are the Request Inbox categories, topics, areas and labels maintained?',
      'How do I map an AI-suggested term to an existing category?',
    ],
  },
  '/backoffice/ai-assistants/request-inbox/routing-rules': {
    keywords: [
      'request inbox routing rules',
      'routing rule',
      'helpdesk team mapping',
      'team map',
      'team codes',
      'odoo team',
      'odoo tags',
      'rule precision',
      'rule preview',
      'B2C_SUPPORT',
      'FLEET_SUPPORT',
    ],
    questions: [
      'Which Odoo helpdesk team does a request category go to?',
      'How precise is an Request Inbox routing rule?',
      'How do I map a ryd team code to an Odoo helpdesk team?',
    ],
  },
  '/backoffice/ai-assistants/request-inbox/sources': {
    keywords: [
      'request inbox sources',
      'source registry',
      'mailboxes',
      'partner-support@ryd.one',
      'collector mailbox',
      'portal requests',
      'training only',
      'not configured',
      'classifier context',
    ],
    questions: [
      'Which mailboxes and stores does the Request Inbox read?',
      'Why is a Request Inbox source shown as not configured?',
      'How do I add a new mailbox to the Request Inbox?',
    ],
    faqs: [
      {
        q: 'How do I add a mailbox to the Request Inbox?',
        a: 'Mailboxes are deploy-time configuration (REQUEST_INBOX_GMAIL_SOURCES in rp-integrations), so a developer adds and deploys it first. The Sources page then shows it as configured, and an admin edits its display name, purposes and classifier context there.',
      },
    ],
  },
  '/backoffice/ai-assistants/request-inbox/feedback': {
    keywords: [
      'request inbox feedback',
      'feedback queue',
      'classification corrections',
      'routing corrections',
      'approve feedback',
      'guidance',
      'avoid in future',
    ],
    questions: [
      'Where do I approve corrections to the Request Inbox classifier?',
      'How does "avoid in future" become classifier guidance?',
    ],
  },
};
